import { useMemo, useState } from "react";
import {
  addDays, addMonths, format, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  differenceInCalendarDays, isSameDay, isSameMonth, isToday,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronLeft, ChevronRight, CalendarDays, Clock3, RotateCcw, CheckCircle2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Empty } from "@/components/KpiCard";
import { StatusBadge } from "@/components/StatusBadge";
import { AppointmentDialog } from "@/components/AppointmentDialog";
import { AppointmentDetailDialog } from "@/components/AppointmentDetailDialog";
import { useResource } from "@/lib/api";
import { STATUS, STATUS_KEYS, toISO, fmtLong } from "@/lib/constants";

const START_H = 7;
const END_H = 21;
const SLOT_H = 36;
const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const toTime = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const statusOf = (a) => a.effective_status || a.status || "agendado";
const isRescheduled = (a) => Boolean(a.rescheduled_from) || statusOf(a) === "reagendado";

const layoutLanes = (items) => {
  const lanes = [];
  return items.map((a) => {
    const s = toMin(a.time);
    let i = lanes.findIndex((end) => end <= s);
    if (i === -1) { i = lanes.length; lanes.push(0); }
    lanes[i] = s + a.duration;
    return { ...a, lane: i };
  }).map((a, _, arr) => ({ ...a, lanes: Math.max(...arr.map((x) => x.lane)) + 1 }));
};

const DayColumn = ({ day, items, onSlot, onOpen }) => {
  const placed = layoutLanes(items);
  const click = (e) => {
    if (e.target !== e.currentTarget) return;
    const slot = Math.floor(e.nativeEvent.offsetY / SLOT_H);
    onSlot(toISO(day), toTime(START_H * 60 + slot * 30));
  };
  return (
    <div className="agenda-grid-bg relative cursor-cell border-l border-slate-100" style={{ height: (END_H - START_H) * 2 * SLOT_H }} onClick={click} data-testid={`agenda-day-${toISO(day)}`}>
      {placed.map((a) => {
        const top = ((toMin(a.time) - START_H * 60) / 30) * SLOT_H;
        const h = Math.max((a.duration / 30) * SLOT_H - 3, 32);
        const status = STATUS[statusOf(a)] || STATUS.agendado;
        return (
          <button key={a.id} onClick={() => onOpen(a)} data-testid={`agenda-appt-${a.id}`}
            className={`absolute overflow-hidden rounded-lg border-l-[3px] px-2 py-1 text-left text-[11px] leading-tight shadow-sm transition-transform duration-150 hover:z-10 hover:scale-[1.02] ${status.block}`}
            style={{ top: top + 1, height: h, left: `calc(${(a.lane / a.lanes) * 100}% + 3px)`, width: `calc(${100 / a.lanes}% - 6px)` }}>
            <p className="font-mono-num font-semibold opacity-70">{a.time}{isRescheduled(a) ? " · ↻" : ""}</p>
            <p className="truncate font-bold">{a.patient_name}</p>
            {h > 50 && <p className="truncate opacity-75">{a.procedure}</p>}
          </button>
        );
      })}
    </div>
  );
};

const Grid = ({ days, appts, onSlot, onOpen }) => (
  <div className="surface overflow-x-auto">
    <div className="min-w-[760px]" style={{ minWidth: days.length === 1 ? 0 : undefined }}>
      <div className="grid border-b border-slate-100" style={{ gridTemplateColumns: `56px repeat(${days.length}, 1fr)` }}>
        <div />
        {days.map((d) => (
          <div key={toISO(d)} className={`px-2 py-3 text-center ${isSameDay(d, new Date()) ? "bg-sky-50/70" : ""}`}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{format(d, "EEE", { locale: ptBR })}</p>
            <p className={`font-display text-lg font-extrabold ${isSameDay(d, new Date()) ? "text-sky-600" : "text-slate-800"}`}>{format(d, "dd")}</p>
          </div>
        ))}
      </div>
      <div className="grid" style={{ gridTemplateColumns: `56px repeat(${days.length}, 1fr)` }}>
        <div>{Array.from({ length: END_H - START_H }, (_, i) => (
          <div key={i} className="font-mono-num pr-2 text-right text-[10px] text-slate-400" style={{ height: SLOT_H * 2 }}>{String(START_H + i).padStart(2, "0")}:00</div>
        ))}</div>
        {days.map((d) => <DayColumn key={toISO(d)} day={d} items={appts.filter((a) => a.date === toISO(d))} onSlot={onSlot} onOpen={onOpen} />)}
      </div>
    </div>
  </div>
);

const MonthView = ({ days, cursor, appts, onSlot, onOpen }) => (
  <div className="surface overflow-hidden" data-testid="agenda-month-view">
    <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/80">
      {["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"].map((d) => (
        <div key={d} className="px-1 py-3 text-center text-xs font-bold uppercase tracking-wide text-slate-500 sm:px-3">{d}</div>
      ))}
    </div>
    <div className="grid grid-cols-7">
      {days.map((day) => {
        const dayItems = appts.filter((a) => a.date === toISO(day)).sort((a, b) => a.time.localeCompare(b.time));
        const inMonth = isSameMonth(day, cursor);
        return (
          <div key={toISO(day)} className={`min-h-[112px] border-b border-r border-slate-100 p-1.5 transition-colors sm:min-h-[145px] sm:p-2 ${inMonth ? "bg-white" : "bg-slate-50/70"} ${isToday(day) ? "ring-2 ring-inset ring-sky-400" : ""}`} data-testid={`agenda-month-day-${toISO(day)}`}>
            <button type="button" onClick={() => onSlot(toISO(day), "09:00")} className={`mb-1 grid h-7 w-7 place-items-center rounded-full text-xs font-bold hover:bg-sky-100 ${inMonth ? "text-slate-700" : "text-slate-300"} ${isToday(day) ? "bg-sky-600 text-white hover:bg-sky-700" : ""}`} aria-label={`Novo agendamento em ${toISO(day)}`}>
              {format(day, "d")}
            </button>
            <div className="space-y-1">
              {dayItems.slice(0, 4).map((a) => {
                const status = STATUS[statusOf(a)] || STATUS.agendado;
                const rescheduled = isRescheduled(a);
                return (
                  <button type="button" key={a.id} onClick={() => onOpen(a)} title={`${a.time} · ${a.patient_name} · ${a.procedure}`} data-testid={`agenda-month-appt-${a.id}`}
                    className={`block w-full truncate rounded-md border px-1.5 py-1 text-left text-[10px] font-semibold leading-tight sm:text-xs ${rescheduled ? "border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100" : `${status.badge} hover:brightness-[0.98]`}`}>
                    <span className="font-mono-num">{a.time}</span> {rescheduled && <span aria-label="Reagendada">↻ </span>}{a.patient_name}
                  </button>
                );
              })}
              {dayItems.length > 4 && <button type="button" onClick={() => onSlot(toISO(day), "09:00")} className="px-1 text-[10px] font-bold text-sky-700 hover:underline">+{dayItems.length - 4} consultas</button>}
            </div>
            {inMonth && dayItems.length === 0 && <button type="button" onClick={() => onSlot(toISO(day), "09:00")} className="mt-1 hidden w-full rounded-md py-1 text-left text-[10px] text-slate-300 hover:bg-sky-50 hover:text-sky-600 sm:block"><Plus className="mr-1 inline h-3 w-3" /> Agendar</button>}
          </div>
        );
      })}
    </div>
  </div>
);

const ListView = ({ days, appts, onOpen }) => (
  <div className="space-y-4" data-testid="agenda-list">
    {days.map((d) => {
      const items = appts.filter((a) => a.date === toISO(d));
      if (!items.length) return null;
      return (
        <div key={toISO(d)} className="surface p-4">
          <p className="mb-2 text-sm font-bold text-slate-700">{fmtLong(toISO(d))}</p>
          {items.map((a) => (
            <button key={a.id} onClick={() => onOpen(a)} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-slate-50" data-testid={`agenda-list-item-${a.id}`}>
              <span className="font-mono-num w-12 text-sm font-semibold text-slate-500">{a.time}</span>
              <span className={`h-8 w-1 rounded-full ${(STATUS[statusOf(a)] || STATUS.agendado).dot}`} />
              <span className="flex-1"><b className="text-slate-800">{a.patient_name}</b> <span className="text-sm text-slate-500">· {a.procedure}</span>{isRescheduled(a) && <span className="ml-2 text-xs font-semibold text-violet-600">↻ Reagendada</span>}</span>
              <StatusBadge status={statusOf(a)} />
            </button>
          ))}
        </div>
      );
    })}
    {!appts.length && <Empty>Nenhuma consulta neste período</Empty>}
  </div>
);

export default function Agenda() {
  const [view, setView] = useState("mes");
  const [cursor, setCursor] = useState(new Date());
  const [filter, setFilter] = useState("todos");
  const [newInit, setNewInit] = useState(null);
  const [sel, setSel] = useState(null);

  const days = useMemo(() => {
    if (view === "dia") return [cursor];
    if (view === "mes") {
      const first = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
      const last = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
      return Array.from({ length: differenceInCalendarDays(last, first) + 1 }, (_, i) => addDays(first, i));
    }
    const s = startOfWeek(cursor, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(s, i));
  }, [view, cursor]);

  const { data } = useResource(`/appointments?start=${toISO(days[0])}&end=${toISO(days[days.length - 1])}`);
  const all = data || [];
  const inMonth = all.filter((a) => isSameMonth(new Date(`${a.date}T12:00:00`), cursor));
  const filtered = filter === "todos" ? all : all.filter((a) => filter === "reagendado" ? isRescheduled(a) : statusOf(a) === filter);
  const step = view === "dia" ? 1 : view === "mes" ? 0 : 7;
  const counts = Object.fromEntries(STATUS_KEYS.map((k) => [k, inMonth.filter((a) => k === "reagendado" ? isRescheduled(a) : statusOf(a) === k).length]));
  const visibleDays = view === "lista" ? days : days;

  const move = (direction) => setCursor((current) => view === "mes" ? addMonths(current, direction) : addDays(current, direction * step));
  const title = view === "mes" ? format(cursor, "MMMM 'de' yyyy", { locale: ptBR }) : view === "dia" ? fmtLong(toISO(cursor)) : `Semana de ${format(days[0], "dd/MM")} a ${format(days[days.length - 1], "dd/MM/yyyy")}`;

  return (
    <div>
      <PageHeader title="Agendamentos" subtitle={title}>
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="bg-sky-50">
            <TabsTrigger value="mes" data-testid="agenda-view-month">Mês</TabsTrigger>
            <TabsTrigger value="semana" data-testid="agenda-view-week">Semana</TabsTrigger>
            <TabsTrigger value="dia" data-testid="agenda-view-day">Dia</TabsTrigger>
            <TabsTrigger value="lista" data-testid="agenda-view-list">Lista</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" onClick={() => move(-1)} data-testid="agenda-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => setCursor(new Date())} data-testid="agenda-today-btn">Hoje</Button>
          <Button variant="outline" size="icon" onClick={() => move(1)} data-testid="agenda-next-btn"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </PageHeader>

      {view === "mes" && (
        <div className="mb-5 grid grid-cols-2 gap-3 xl:grid-cols-4" data-testid="agenda-month-summary">
          <div className="rounded-2xl border border-sky-100 bg-sky-50 p-3 sm:p-4"><div className="flex items-center gap-2 text-sky-700"><CalendarDays className="h-4 w-4" /><span className="text-xs font-semibold">Consultas no mês</span></div><p className="mt-2 text-2xl font-extrabold text-sky-900">{inMonth.length}</p></div>
          <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-3 sm:p-4"><div className="flex items-center gap-2 text-indigo-700"><Clock3 className="h-4 w-4" /><span className="text-xs font-semibold">Agendadas</span></div><p className="mt-2 text-2xl font-extrabold text-indigo-900">{counts.agendado}</p></div>
          <div className="rounded-2xl border border-violet-100 bg-violet-50 p-3 sm:p-4"><div className="flex items-center gap-2 text-violet-700"><RotateCcw className="h-4 w-4" /><span className="text-xs font-semibold">Reagendadas</span></div><p className="mt-2 text-2xl font-extrabold text-violet-900">{counts.reagendado}</p></div>
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-3 sm:p-4"><div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" /><span className="text-xs font-semibold">Concluídas</span></div><p className="mt-2 text-2xl font-extrabold text-emerald-900">{counts.concluido}</p></div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2" data-testid="agenda-status-filters">
        <button onClick={() => setFilter("todos")} data-testid="agenda-filter-todos" className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${filter === "todos" ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 bg-white text-slate-600"}`}>Todos · {view === "mes" ? inMonth.length : all.length}</button>
        {STATUS_KEYS.map((k) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`agenda-filter-${k}`}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${filter === k ? STATUS[k].badge + " ring-2 ring-offset-1 ring-slate-200" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
            <span className={`h-2 w-2 rounded-full ${STATUS[k].dot}`} /> {k === "reagendado" ? "Reagendadas" : STATUS[k].label} · {view === "mes" ? counts[k] : all.filter((a) => k === "reagendado" ? isRescheduled(a) : statusOf(a) === k).length}
          </button>
        ))}
      </div>

      {view === "mes"
        ? <MonthView days={days} cursor={cursor} appts={filtered} onSlot={(date, time) => setNewInit({ date, time })} onOpen={setSel} />
        : view === "lista"
          ? <ListView days={visibleDays} appts={filtered} onOpen={setSel} />
          : <Grid days={days} appts={filtered} onSlot={(date, time) => setNewInit({ date, time })} onOpen={setSel} />}

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
        <span className="font-semibold text-slate-600">Legenda:</span>
        {STATUS_KEYS.map((k) => <span key={k} className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-sm ${STATUS[k].dot}`} />{STATUS[k].label}</span>)}
        <span className="inline-flex items-center gap-1.5 text-violet-700"><RotateCcw className="h-3 w-3" />Reagendada</span>
      </div>
      <p className="mt-3 text-xs text-slate-400">Clique em uma consulta para ver os detalhes. Clique no número de um dia ou em um horário vazio para criar um agendamento.</p>
      <AppointmentDialog open={!!newInit} onOpenChange={(v) => !v && setNewInit(null)} initial={newInit} />
      <AppointmentDetailDialog appointment={sel} open={!!sel} onOpenChange={(v) => !v && setSel(null)} />
    </div>
  );
}
